import { View } from 'react-native';
import { Image } from 'expo-image';
import { observer } from 'mobx-react-lite';
import { TestIDs } from 'src/constants/testIDs.constants';
import { useStore } from 'src/contexts/StoreContext';
import { modalTestID } from 'src/helpers/testIDs.helper';
import { brand, neutrals } from 'src/theme/colors';
import Button from '@photosi/rn-components/buttons/button/Button';
import { FocusGroups } from '@photosi/rn-components/focus-provider/FocusProvider.constants';
import { Icon } from '@photosi/rn-components/icon/Icon';
import TextLato from '@photosi/rn-components/text/text-lato/TextLato';
import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';
import { stylesDesktop, stylesMobile } from './StepCard.style';
import { LinearGradient } from 'expo-linear-gradient';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

export interface StepCardProps {
  code: string;
  title: string;
  price?: string;
  description?: string;
  previewImageUrl: string;
  index: number;
  isActive?: boolean;
  isDisabled?: boolean;
  isSelected?: boolean;
  onPress?: () => void;
  avoidRadioButton?: boolean;
  isSuggested?: boolean;
  width: number;
  borderColor?: string;
  backgroundColor?: string;
  forceVertical?: boolean;
  isSuboptimal?: boolean;
  imageHeight?: number;
  boldOnSelect?: boolean;
  isLookupChoice?: boolean;
  textSuggested?: string;
}

export const StepCard = observer(
  ({
    code,
    title,
    price,
    description,
    previewImageUrl,
    index,
    isActive,
    isDisabled,
    isSelected,
    onPress,
    avoidRadioButton,
    isSuggested,
    width,
    borderColor = `${neutrals.white}00`,
    backgroundColor = neutrals.white,
    forceVertical,
    isSuboptimal,
    imageHeight,
    boldOnSelect = true,
    isLookupChoice = false,
    textSuggested = 'modal-configuration-steps:best-seller',
  }: StepCardProps) => {
    const { i18n } = useStore();
    const { isDesktop } = useResponsiveScreen();
    const styles = isDesktop && !forceVertical ? stylesDesktop : stylesMobile;
    return (
      <Button
        onPress={async () => onPress && onPress()}
        testID={modalTestID(TestIDs.CONFIGURATION_STEPS, index.toString())}
        focusId={modalTestID(TestIDs.CONFIGURATION_STEPS, index.toString())}
        focusGroup={FocusGroups.PRODUCT}
        focusOrder={index}
        key={`step-card_${code}`}
        focusable={isActive}
        disabled={isDisabled}
        accessibilityLabel={title}
        accessibilityRole="radio"
        accessibilityState={{ checked: isSelected, disabled: isDisabled }}
        style={{ borderRadius: 20 }}
      >
        {({ focused }) => {
          return (
            <View style={[styles.card, { width, backgroundColor }, isDisabled && styles.imageDisabled]}>
              {isSuboptimal && (
                <View style={[styles.subTag]}>
                  <View style={styles.icon}>
                    <Icon name={ICON_NAMES.Alert} color={neutrals.white} />
                  </View>
                  <TextLato style={styles.subTagText}>{i18n.t('lookups:suboptimal')}</TextLato>
                </View>
              )}
              {isSuggested && (
                <LinearGradient
                  colors={[brand.main.water, brand.main.variations['darkerIris-1']]}
                  style={styles.badge}
                  pointerEvents="none"
                  start={{ x: 1, y: 0 }}
                  end={{ x: 0, y: 0 }}
                >
                  <View style={[styles.icon, { padding: 2 }]}>
                    <Icon name={ICON_NAMES.Star} color={neutrals.white} filled />
                  </View>
                  <TextLato style={styles.textBadge}>{i18n.t(textSuggested)}</TextLato>
                </LinearGradient>
              )}
              <View
                style={[
                  { borderColor, width: '100%' },
                  styles.border,
                  isSelected && styles.borderSelected,
                  // focused && !isSelected && styles.borderFocused,
                ]}
              >
                <Image
                  style={[
                    styles.image,
                    imageHeight ? { width: '100%', height: imageHeight } : { width: '100%', aspectRatio: 1.15 },
                  ]}
                  source={{ uri: previewImageUrl }}
                  contentFit={imageHeight ? 'cover' : 'fill'}
                  accessible={false}
                />
                {!avoidRadioButton && (
                  <View style={[styles.radioButton, isSelected && { borderColor: brand.main.water }]}>
                    {isSelected && <View style={styles.radioButtonSelected} />}
                  </View>
                )}
                <View style={styles.bottomContainer}>
                  <View style={styles.textContainer}>
                    <View
                      style={
                        (!price?.length || avoidRadioButton) && !description
                          ? styles.textWrapperCenter
                          : styles.textWrapperLeft
                      }
                    >
                      <TextLato
                        numberOfLines={isLookupChoice ? 2 : 1}
                        style={!isSelected ? styles.textTitle : styles.textTitleSelected}
                      >
                        {title}
                      </TextLato>
                    </View>
                  </View>
                  {!avoidRadioButton && !!price?.length && (
                    <View style={styles.textWrapperRight}>
                      <TextLato numberOfLines={1} style={styles.price}>
                        {price}
                      </TextLato>
                    </View>
                  )}
                  {!!description && (
                    <View style={styles.textDescriptionLeft}>
                      <TextLato
                        numberOfLines={2}
                        style={isSelected && boldOnSelect ? styles.descriptionSelected : styles.description}
                      >
                        {description}
                      </TextLato>
                    </View>
                  )}
                </View>
              </View>
            </View>
          );
        }}
      </Button>
    );
  },
);
